<script setup lang="js">

import StringUtils from "@/utils/string.utils.js";
import InputText from "primevue/inputtext";
import Slider from "primevue/slider";
import { ref, watch } from "vue";

const props = defineProps(['item', 'technologicalAreas', 'courses', 'subfunctions', 'capacities', 'knowledge', 'difficultyLevels', 'formats',
  'openSupportImagesDialog', 'onTechnologicalAreaChange', 'onCourseChange', 'onSubfunctionChange', 'onCapacityChange', 'isDisableKnowledge',
  'generateTags', 'isGeneratingTags', 'tagsMessage', 'resetItem'])

const newTag = ref('')
const sliderValue = ref(2)

watch(() => props.item.information.difficultyLevel, (newValue) => {
  if (newValue && newValue.value) {
    sliderValue.value = newValue.value
  }
}, { immediate: true })

const updateDifficultyLevel = (value) => {
  const selectedLevel = props.difficultyLevels.find(level => level.value === value)
  if (selectedLevel) {
    props.item.information.difficultyLevel = selectedLevel
  }
}

const addNewTag = () => {
  if (StringUtils.isBlank(newTag.value)) {
    return;
  }

  const tag = newTag.value.trim();
  if (!props.item.information.tags.includes(tag)) {
    props.item.information.tags.push(tag)
  }
  newTag.value = '';
}

</script>

<template>
  <div class="menu-width p-4">
    <div class="header-section mb-4">
      <h4 class="header-title">Docentes</h4>
      <p class="header-subtitle">Selecione as informações essenciais para gerar um novo item de avaliação</p>
    </div>
    

    <div class="form-grid">
      <div class="field-group">
        <label for="technologicalArea" class="field-label">
          <i class="pi pi-building mr-2"></i>
          Área Tecnológica
        </label>
        <Select
            id="technologicalArea"
            v-model="props.item.information.technologicalArea"
            :options="props.technologicalAreas"
            @change="props.onTechnologicalAreaChange"
            optionLabel="name"
            placeholder="Selecione a área tecnológica"
            class="field-input"
            :loading="!props.technologicalAreas.length > 0"
        />
      </div>
      
      <div class="field-group">
        <label for="course" class="field-label">
          <i class="pi pi-graduation-cap mr-2"></i>
          Curso
        </label>
        <Select
            id="course"
            v-model="props.item.information.course"
            :options="props.courses[props.item.information.technologicalArea?.idTechnologicalArea]"
            @change="props.onCourseChange"
            optionLabel="name"
            placeholder="Selecione o curso"
            :disabled="!props.item.information.technologicalArea"
            class="field-input"
        />
      </div>
      
      <div class="field-group">
        <label for="capacity" class="field-label">
          <i class="pi pi-cog mr-2"></i>
          Capacidade
        </label>
        <Select
            id="capacity"
            v-model="props.item.information.capacity"
            :options="props.capacities"
            @change="props.onCapacityChange"
            optionLabel="name"
            placeholder="Selecione a capacidade"
            :disabled="!props.item.information.course"
            class="field-input"
        />
      </div>
      
      <div class="field-group">
        <label for="subfunction" class="field-label">
          <i class="pi pi-sitemap mr-2"></i>
          Subfunção
        </label>
        <Select
            id="subfunction"
            v-model="props.item.information.subfunction"
            :options="props.subfunctions[props.item.information.capacity?.idCapacity]"
            @change="props.onSubfunctionChange"
            optionLabel="name"
            placeholder="Selecione a subfunção"
            :disabled="!props.item.information.capacity"
            class="field-input"
        />
      </div>
      
      <div class="field-group">
        <label for="knowledge" class="field-label">
          <i class="pi pi-book mr-2"></i>
          Conhecimentos
        </label>
        <MultiSelect
            id="knowledge"
            v-model="props.item.information.knowledge"
            :options="(props.knowledge[props.item.information.capacity?.idCapacity] || {})[props.item.information.subfunction?.idSubfunction]"
            optionLabel="name"
            placeholder="Selecione os conhecimentos"
            :disabled="props.isDisableKnowledge()"
            display="chip"
            class="field-input"
        />
      </div>
      
      <div class="field-group">
        <label for="difficultyLevel" class="field-label">
          <i class="pi pi-chart-line mr-2"></i>
          Nível de dificuldade
        </label>
        <div class="difficulty-slider-container">
          <InputText
              id="difficultyLevel"
              :value="props.item.information.difficultyLevel?.label || 'Selecione o nível de dificuldade'"
              readonly
              placeholder="Selecione o nível de dificuldade"
              class="field-input difficulty-display"
          />
          <Slider
              v-model="sliderValue"
              :min="1"
              :max="3"
              :step="1"
              class="difficulty-slider"
              @update:modelValue="updateDifficultyLevel"
          />
        </div>
      </div>
      
      <div class="field-group">
        <label class="field-label">
          <i class="pi pi-list mr-2"></i>
          Formato do item
        </label>
        <div class="radio-group">
          <div v-for="(format, index) in props.formats" :key="format.value" class="radio-item">
            <RadioButton
                :id="'format' + index"
                v-model="props.item.information.format"
                :value="format"
                class="radio-input"
            />
            <label :for="format.value" class="radio-label">{{ format.label }}</label>
          </div>
        </div>
      </div>
      
      <div class="field-group">
        <label for="numberOfAlternatives" class="field-label">
          <i class="pi pi-sort-numeric-up mr-2"></i>
          Quantidade de alternativas
        </label>
        <InputNumber
            id="numberOfAlternatives"
            v-model="props.item.information.numberOfAlternatives"
            :showButtons="true"
            :min="5"
            :max="5"
            disabled
            class="field-input number-input"
        />
      </div>
    </div>

    <div class="support-image-section" v-if="!props.item.information.supportImages?.length">
      <Button
          id="prepareNewSupportImages"
          class="support-image-btn"
          icon="pi pi-upload"
          label="Adicionar Imagem de Suporte"
          @click="props.openSupportImagesDialog"
          severity="secondary"
          outlined
      />
    </div>

    <div class="tags-section">
      <Fieldset id="tags" legend="Tags" class="tags-fieldset">
        <template #legend>
          <div class="fieldset-legend">
            <i class="pi pi-tags mr-2"></i>
            <span>Tags</span>
          </div>
        </template>
        
        <div class="tags-container">
          <div v-for="tag in props.item.information.tags" :key="tag" class="tag-item">
            <Chip
                :label="tag"
                class="tag-chip"
                @remove="props.item.information.tags.splice(props.item.information.tags.indexOf(tag), 1)"
                removable
            />
          </div>
        </div>

        <div class="tags-actions">
          <Button
              id="generateTags"
              label="Sugerir Tags"
              icon="pi pi-sparkles"
              class="generate-tags-btn"
              :disabled="props.isGeneratingTags"
              :loading="props.isGeneratingTags"
              @click="props.generateTags()"
              severity="info"
              outlined
          />

          <InputGroup class="tag-input-group">
            <InputText
                id="newTag"
                v-model="newTag"
                class="tag-input"
                placeholder="Nova tag personalizada"
                @keydown.enter="addNewTag()"/>
            <Button
                id="addNewTag"
                icon="pi pi-plus"
                class="add-tag-btn"
                @click="addNewTag()"
                severity="success"
                outlined/>
          </InputGroup>
        </div>

        <div 
            id="tagsMessage"
            v-if="props.isGeneratingTags || props.tagsMessage.message"
            :class="['tags-message', { 'error': props.tagsMessage.error, 'success': !props.tagsMessage.error }]"
            v-html="props.tagsMessage.message || (props.isGeneratingTags ? 'Gerando tags...' : '')"
        />
      </Fieldset>
    </div>

    <div class="actions-section">
      <Button
          id="clearFields"
          label="Limpar Campos"
          icon="pi pi-refresh"
          @click="props.resetItem()"
          severity="danger"
          text
          class="clear-btn"
      />
    </div>
  </div>
</template>

<style scoped>
.menu-width {
  max-width: none !important;
  width: 100% !important;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-section {
  text-align: center;
  padding: 0 0 1rem 0;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(229, 231, 235, 0.6);
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f5631a;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
}

.header-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.custom-divider {
  margin: 1.5rem 0 !important;
  opacity: 0.6;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;
  margin-bottom: 0.25rem;
}

.field-label i {
  color: #f5631a;
  font-size: 0.875rem;
}

.field-input {
  width: 100% !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.difficulty-slider-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.difficulty-display {
  background-color: #f9fafb !important;
  cursor: default !important;
}

.difficulty-slider {
  width: 100% !important;
  margin: 0.5rem 0 !important;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 3px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

}

.radio-input {
  margin: 0 !important;
}

.radio-label {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
}

.number-input {
  width: 100% !important;
}

.support-image-section {
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
}

.support-image-btn {
  width: 100% !important;
  padding: 0.875rem !important;
  font-weight: 500 !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
}

.tags-section {
  margin: 1.5rem 0;
}

.tags-fieldset {
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  border-radius: 5px !important;
  background: rgb(255, 255, 255) !important;
  backdrop-filter: blur(10px) !important;
}

.fieldset-legend {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.fieldset-legend i {
  color: #f5631a;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  min-height: 2rem;
  align-items: flex-start;
}

.tag-item {
  animation: fadeInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tag-chip {
  background: rgba(245, 99, 26, 0.1) !important;
  color: #c23a0d !important;
  border: 1px solid rgba(245, 99, 26, 0.2) !important;
  font-weight: 500 !important;
}

.tags-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.generate-tags-btn {
  width: 100% !important;
  font-weight: 500 !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
}

.tag-input-group {
  width: 100%;
}

.tag-input {
  flex: 1 !important;
}

.add-tag-btn {
  border-left: none !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
}

.tags-message {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 3px;
  font-size: 0.875rem;
  line-height: 1.5;
  letter-spacing: -0.01em;
  animation: fadeInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &.error {
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
    border: 1px solid rgba(220, 38, 38, 0.2);
  }
  
  &.success {
    background: rgba(34, 197, 94, 0.1);
    color: #059669;
    border: 1px solid rgba(34, 197, 94, 0.2);
  }
}

.actions-section {
  display: flex;
  justify-content: center;
  padding-top: 1rem;
  margin-top: 1.5rem;
  border-top: 1px solid rgba(229, 231, 235, 0.6);
}

.clear-btn {
  font-weight: 500 !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .form-grid {
    gap: 1rem;
  }
  
  .header-section {
    padding: 0 0 0.75rem 0;
    margin-bottom: 0.75rem;
  }
  
  .header-title {
    font-size: 1.125rem;
  }
  
  .tags-section,
  .support-image-section {
    margin: 1rem 0;
  }
  
  .custom-divider {
    margin: 1rem 0 !important;
  }
}
</style>
